import { useBranding } from '../../contexts/BrandingContext';

/**
 * PlatformLogo — Tenant-aware logo component
 *
 * Renders different logo SVGs based on branding context:
 * - ∑n-route: Constellation path leading to North Star
 * - EazyCourseEngine: Globe with wave stripes
 * - GuruSkool: Globe with wave stripes
 *
 * @param {number} size - Logo size in pixels (default: 36)
 * @param {boolean} showName - Show platform name next to logo (default: true)
 * @param {string} className - Additional CSS classes
 */
export default function PlatformLogo({ size = 36, showName = true, className = '' }) {
  const branding = useBranding();

  // Determine which logo to render
  const isEnroute = branding.platform_name === '∑n-route' || branding.platform_plain_name === 'nroute';
  const isEazyCourse = branding.platform_name === 'EazyCourseEngine';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {branding.logo_url ? (
        <img
          src={branding.logo_url}
          alt={branding.platform_name}
          width={size}
          height={size}
        />
      ) : isEnroute ? (
        <EnrouteConstellationLogo size={size} />
      ) : (
        <GlobeSVG
          size={size}
          primaryColor={branding.primary_color}
          secondaryColor={branding.secondary_color}
        />
      )}
      {showName && (
        <span
          className="font-semibold text-lg"
          style={{ color: 'var(--brand-primary)' }}
        >
          {branding.platform_name}
        </span>
      )}
    </div>
  );
}

/**
 * EnrouteConstellationLogo — ∑n-route North Star logo (v2.39.4)
 *
 * Design:
 * - Gold North Star (#C9A84C, Midnight Observatory palette) as focal point
 * - Soft glow halo (concentric circles at 12% and 20% opacity)
 * - No container, no constellation trail
 * - Star sized at 48% of viewBox for prominence
 *
 * Symbolism: "Your global north star" (tagline) — destination focus, not journey
 *
 * Bug fix: Previous navy container (#0B2540) was invisible on dark backgrounds.
 * Ref: incidents/2026-04-16-rollback-v2.39.2.md
 */
function EnrouteConstellationLogo({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      {/* Outer glow halo - soft prominence */}
      <circle cx="50" cy="50" r="27" fill="#C9A84C" opacity="0.12"/>

      {/* Inner glow halo - stronger definition */}
      <circle cx="50" cy="50" r="21" fill="#C9A84C" opacity="0.20"/>

      {/* North Star - 48% of viewBox, centered */}
      <polygon
        points="50,26 55,42 72,44 61,54 64,71 50,62 36,71 39,54 28,44 45,42"
        fill="#C9A84C"
      />
    </svg>
  );
}

/**
 * GlobeSVG — Dynamic globe logo with configurable colors
 *
 * Based on the original GlobeMark.jsx design:
 * - Blue globe circle with meridian lines
 * - Orange wave stripes (3 sinuous bands)
 * - Orange spark dot accent
 *
 * @param {number} size - SVG size in pixels
 * @param {string} primaryColor - Globe circle and meridian color (default: var(--brand-primary))
 * @param {string} secondaryColor - Wave stripes and spark color (default: var(--brand-secondary))
 */
function GlobeSVG({ size, primaryColor = 'var(--brand-primary)', secondaryColor = 'var(--brand-secondary)' }) {
  const scale = size / 40; // Original design is 40x40

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Platform Logo"
    >
      <defs>
        {/* Clip path for the globe circle */}
        <clipPath id={`globe-clip-${size}`}>
          <circle cx="20" cy="20" r="17.5" fill="white" />
        </clipPath>
      </defs>

      {/* Background circle - globe body */}
      <circle cx="20" cy="20" r="18" fill={primaryColor} />

      {/* Wave stripes - clipped to globe circle */}
      <g clipPath={`url(#globe-clip-${size})`}>
        {/* Top wave */}
        <path
          d="M 0,12 Q 10,8 20,12 T 40,12 L 40,18 Q 30,14 20,18 T 0,18 Z"
          fill={secondaryColor}
        />
        {/* Middle wave */}
        <path
          d="M 0,20 Q 10,16 20,20 T 40,20 L 40,26 Q 30,22 20,26 T 0,26 Z"
          fill={secondaryColor}
        />
        {/* Bottom wave */}
        <path
          d="M 0,28 Q 10,24 20,28 T 40,28 L 40,34 Q 30,30 20,34 T 0,34 Z"
          fill={secondaryColor}
        />
      </g>

      {/* Meridian lines */}
      <g opacity="0.2">
        <ellipse cx="20" cy="20" rx="7" ry="17" fill="none" stroke={primaryColor} strokeWidth="0.5" />
        <ellipse cx="20" cy="20" rx="14" ry="17" fill="none" stroke={primaryColor} strokeWidth="0.5" />
      </g>

      {/* Latitude line */}
      <ellipse cx="20" cy="20" rx="17" ry="8" fill="none" stroke={primaryColor} strokeWidth="0.5" opacity="0.2" />

      {/* Spark accent dot */}
      <circle cx="33" cy="8" r="2" fill={secondaryColor} />
    </svg>
  );
}
